import React, { useEffect, useState } from 'react';
import { base44 } from "@/api/base44Client";
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { Card } from "@/components/ui/card";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Tooltip, TooltipTrigger, TooltipContent, TooltipProvider } from "@/components/ui/tooltip";
import { Plus, MapPin, Calendar, Pencil, Check, X, ToggleLeft, ToggleRight, Trash2, Download, Clock, CheckCircle2, XCircle, Users, UserPlus, Eye, EyeOff, FileText, ExternalLink, AlertTriangle, Lock } from "lucide-react";
import { Link, useSearchParams } from 'react-router-dom';
import { useAuth } from '@/lib/AuthContext';
import ShowSelector from '@/components/ShowSelector';
import { motion, AnimatePresence } from "framer-motion";
import { toast } from "sonner";
import { formatInZone } from '@/lib/serverTime';
import { timezoneForContact, formatShowDateRange } from '@/lib/showTimezone';
import SheetPanel from '@/components/SheetPanel';
import PageContainer from '@/components/PageContainer';

/**
 * A blank show form.
 *
 * The show's own message wording (the text message and all four emails)
 * is not part of this form. It moved to the Templates screen's Per-Event
 * Override panel (task 9 of the 2026-09-06 batch), which already had all
 * four emails plus the inherited/override labeling; this form used to have
 * room for only the text message and the Initial Email, so the three
 * follow up reminders could never be worded here at all.
 */
const EMPTY_SHOW = {
  name: '',
  location: '',
  start_date: '',
  end_date: '',
  notes: '',
  is_active: true,
  doc_url: '',
  doc_name: '',
  industry: '',
  industry_other: '',
  meeting_topics_placeholder: '',
};

/**
 * Metadata only (migration 0007). Not read by resolveTemplates() or
 * anything else yet, see worker/routes/shows.ts.
 */
const INDUSTRY_OPTIONS = [
  { value: 'automotive', label: 'Automotive' },
  { value: 'restaurants', label: 'Restaurants' },
  { value: 'fitness', label: 'Fitness' },
  { value: 'hotels', label: 'Hotels' },
  { value: 'other', label: 'Other' },
];

/**
 * Every required field on this form, in one place, so validation, the
 * asterisk/"Required" caption, and the missing fields modal can never say
 * three different things about what is actually required.
 *
 * location/start_date/end_date, task: Home screen redesign (2026-09-08).
 * Required on a real trade show only: the new header banner shows a show's
 * location and date range directly, and a general bucket has neither by
 * design (it is timeless, no venue), so isGeneral skips these three rather
 * than asking for something that cannot exist.
 */
const REQUIRED_FIELDS = [
  { key: 'name', label: 'Show Name', missing: (f) => !f.name.trim() },
  {
    key: 'industry_other',
    label: 'Industry name',
    missing: (f) => f.industry === 'other' && !f.industry_other.trim(),
  },
  { key: 'location', label: 'Location', missing: (f, isGeneral) => !isGeneral && !f.location.trim() },
  { key: 'start_date', label: 'Start Date', missing: (f, isGeneral) => !isGeneral && !f.start_date },
  { key: 'end_date', label: 'End Date', missing: (f, isGeneral) => !isGeneral && !f.end_date },
];

function getMissingFields(form, isGeneral = false) {
  return REQUIRED_FIELDS.filter((field) => field.missing(form, isGeneral));
}

export default function Shows() {
  const { isAdmin } = useAuth();
  const queryClient = useQueryClient();
  const [searchParams, setSearchParams] = useSearchParams();
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ ...EMPTY_SHOW });
  // Which required fields Save found missing, shown in a modal by label
  // rather than left to a silently unresponsive button.
  const [missingFields, setMissingFields] = useState([]);
  // Which reps are checked in the assignment list below, task: per-show rep
  // assignment (2026-09-15). Separate from `form`: this saves through its
  // own endpoint (PUT /api/shows/:id/members), not the show's own fields.
  const [memberIds, setMemberIds] = useState([]);

  const { data: shows = [], isLoading } = useQuery({
    queryKey: ['shows'],
    queryFn: () => base44.entities.TradeShow.list('-start_date', 50),
  });

  // A general bucket's identity is locked server-side (worker/routes/shows.ts):
  // name, dates and archiving cannot change. The form disables exactly those
  // fields when editing one, but the form still holds their current value, so
  // submitting the whole `form` object would send them back unchanged and the
  // API would 400 anyway, since it cannot tell "unchanged" from "an edit" ~
  // both are just the field being present in the request. Editing a general
  // bucket strips them from what is actually sent instead.
  //
  // Derived up here, not further down, because the rep-assignment queries
  // right below need it too: a personal bucket can never take a rep
  // assignment (worker/routes/shows.ts refuses it), so there is nothing to
  // fetch or show for one.
  const editingShow = editing ? shows.find((s) => s.id === editing) : null;
  const editingIsGeneral = editingShow?.is_general ?? false;

  // The four (or however many) reps to offer as checkboxes. Admin only,
  // same gate as the assignment UI itself below; GET /api/reps does not
  // require admin on its own, but there is no reason to fetch this for
  // anyone who cannot act on it.
  const { data: allReps = [] } = useQuery({
    queryKey: ['reps-for-assignment'],
    queryFn: () => base44.reps.list(),
    enabled: isAdmin,
  });

  // Loads once per show opened for editing; unrelated to isLoading above,
  // which is the show list itself.
  const { data: currentMemberIds } = useQuery({
    queryKey: ['show-members', editing],
    queryFn: () => base44.entities.TradeShow.getMembers(editing),
    enabled: isAdmin && !!editing && !editingIsGeneral,
  });

  // currentMemberIds loads asynchronously, after the form (and memberIds)
  // are already reset by openEdit below, so the checkboxes pick up the
  // real assignment once it arrives rather than starting from whatever the
  // previously edited show left behind.
  useEffect(() => {
    setMemberIds(currentMemberIds ?? []);
  }, [currentMemberIds]);


  const { data: allMeetings = [] } = useQuery({
    queryKey: ['all-meetings-for-shows'],
    queryFn: async () => {
      const all = await base44.entities.MeetingRequest.list('-created_date', 500);
      return all.filter(m => m.status !== 'deleted');
    },
  });

  const getShowStats = (showId) => {
    const contacts = allMeetings.filter(m => m.trade_show_id === showId);
    return {
      total: contacts.length,
      pending: contacts.filter(m => m.status === 'pending').length,
      accepted: contacts.filter(m => m.status === 'accepted').length,
      declined: contacts.filter(m => m.status === 'declined').length,
      contacts,
    };
  };

  const exportShowCSV = (show) => {
    const { contacts } = getShowStats(show.id);
    const headers = [
      'First Name','Last Name','Company','Title','Email','Cell Phone',
      'Office Phone','Ext','Website','Internal Notes','Captured At (Day)','Captured At (Date/Time)',
      'Proposed Meeting Date','Duration (min)','Status','Scanned By',
      'SMS Sent At','Email Sent At'
    ];
    const rows = contacts.map(m => {
      // Task: 2026-09-25. Each contact's own show timezone, same helper
      // EditContact.jsx and ContactRow.jsx use, not a blanket
      // America/Chicago: a rep exporting a show's contacts should see that
      // show's own wall clock time, not always Central regardless of where
      // the show actually was.
      const tz = timezoneForContact(m);
      return [
        m.first_name, m.last_name, m.company, m.title, m.email, m.phone,
        m.office_phone, m.office_phone_ext, m.website, m.internal_notes,
        formatInZone(m.captured_at, tz, 'EEE'),
        formatInZone(m.captured_at, tz, 'M/d/yyyy h:mm a zzz'),
        formatInZone(m.proposed_date, tz, 'M/d/yyyy h:mm a zzz'),
        m.proposed_duration, m.status, m.scanned_by_name,
        formatInZone(m.sms_sent_at, tz, 'M/d/yyyy h:mm a zzz'),
        formatInZone(m.email_sent_at, tz, 'M/d/yyyy h:mm a zzz'),
      ];
    });
    const csv = [headers, ...rows]
      .map(r => r.map(v => `"${(v || '').toString().replace(/"/g, '""')}"`).join(','))
      .join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `contacts_${show.name.replace(/\s+/g, '_')}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const saveMutation = useMutation({
    mutationFn: async (data) => {
      if (editing) return base44.entities.TradeShow.update(editing, data);
      return base44.entities.TradeShow.create(data);
    },
    // Closing the form, resetting `editing` and the success toast used to
    // live here. Moved into handleSubmit, after every mutation for this
    // submit (this one, and setMembersMutation below) has resolved: doing
    // it here meant `editing` was reset to null the instant the show's own
    // fields saved, which also changes the show-members query's key from
    // ['show-members', <id>] to ['show-members', null] and drops its
    // observer before setMembersMutation's own save/cache-update even
    // runs. That is the root cause of the rep checkboxes reverting to
    // unchecked after Save: reopening Edit Show for the same show re-read
    // a query that was torn down mid-save rather than one still watching
    // the real id all the way through.
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['shows'] });
    },
    // Without this, a rejected save (a locked field on a general bucket, a
    // validation error, a network failure) landed nowhere: the form just sat
    // there with no error and no clue why. This is the actual cause behind
    // "Save is failing silently".
    onError: (err) => {
      toast.error(err?.message || 'Could not save that show.');
    },
  });

  const toggleActive = useMutation({
    // is_active is archived or not. Several shows are active at once, and
    // which one the rep is capturing into is the show picker's business,
    // stored per device. That is why nothing is written to localStorage here.
    mutationFn: async ({ id, is_active }) =>
      base44.entities.TradeShow.update(id, { is_active }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['shows'] }),
    onError: (err) => {
      toast.error(err?.message || 'Could not change that show.');
    },
  });

  const setMembersMutation = useMutation({
    mutationFn: ({ id, userIds }) => base44.entities.TradeShow.setMembers(id, userIds),
    // Write the server's own confirmed set straight into the cache rather
    // than only invalidating it. invalidateQueries only flags a query as
    // stale for whichever observer is watching it *right now*; writing the
    // value directly means the next time Edit Show is opened for this
    // show, ['show-members', id] already holds the real, just-saved data
    // even if nothing was observing that key at the exact moment this
    // resolved.
    onSuccess: (savedIds, variables) => {
      queryClient.setQueryData(['show-members', variables.id], savedIds);
    },
    onError: (err) => {
      toast.error(err?.message || 'Could not save which reps are assigned to that show.');
    },
  });

  const openEdit = (show) => {
    setEditing(show.id);
    // Reset immediately rather than waiting on the show-members query for
    // this show to resolve, so the checkboxes never briefly show whichever
    // show was being edited before this one.
    setMemberIds([]);
    const next = {
      name: show.name || '',
      location: show.location || '',
      start_date: show.start_date || '',
      end_date: show.end_date || '',
      notes: show.notes || '',
      is_active: show.is_active ?? true,
      doc_url: show.doc_url || '',
      doc_name: show.doc_name || '',
      industry: show.industry || '',
      industry_other: show.industry_other || '',
      meeting_topics_placeholder: show.meeting_topics_placeholder || '',
    };
    setForm(next);
    setShowForm(true);
  };

  // Reopens Edit Show for the event named in ?show=, task: fix back
  // navigation from "Edit message wording for this show" (2026-09-22).
  // That screen's own back button now links to `/shows?show=<id>` instead
  // of a hardcoded, origin-blind /admin (Templates.jsx), so landing here
  // needs to round-trip that id back into an open edit form rather than
  // just the bare Shows list. Waits on `shows` actually being loaded
  // (find() over an empty array would never match), and clears the param
  // once consumed (`replace`, no history entry) so it does not fight a
  // later Cancel, and so this never re-fires just because `shows` itself
  // refetches later in the same visit (e.g. after Save).
  useEffect(() => {
    const showId = searchParams.get('show');
    if (!showId || shows.length === 0) return;
    const match = shows.find((s) => s.id === showId);
    setSearchParams({}, { replace: true });
    if (match) openEdit(match);
  }, [searchParams, shows]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    const missing = getMissingFields(form, editingIsGeneral);
    if (missing.length > 0) {
      setMissingFields(missing);
      return;
    }
    try {
      if (editingIsGeneral) {
        const { name, start_date, end_date, is_active, ...editable } = form;
        await saveMutation.mutateAsync(editable);
      } else {
        await saveMutation.mutateAsync(form);
        // Only on an existing show: a brand new one has no id yet, and the
        // checkbox section itself is not shown while creating one (see the
        // rep-assignment block below).
        if (editing) {
          await setMembersMutation.mutateAsync({ id: editing, userIds: memberIds });
        }
      }
      // Only close and reset once every mutation above has actually
      // finished, so `editing` (and the show-members query keyed on it)
      // stays pointed at the real show for as long as saving its members
      // needs it. See saveMutation's own onSuccess for why this used to
      // live there instead.
      setShowForm(false);
      setEditing(null);
      setForm({ ...EMPTY_SHOW });
      toast.success(editing ? 'Show updated' : 'Show created');
    } catch {
      // Each mutation already shows its own toast on failure, above.
    }
  };

  return (
    <div className="min-h-screen bg-background safe-top pb-24">
      <PageContainer className="px-4 py-6">
        <div className="flex items-center gap-3 mb-6">
          <Link to="/">
            <Button variant="outline" className="min-h-9 px-4 text-sm">Back</Button>
          </Link>
          <div className="flex-1">
            <h1 className="text-xl font-bold text-foreground">Trade Shows</h1>
            <p className="text-sm text-muted-foreground">Manage your conference list</p>
          </div>
          {isAdmin && (
            <Button
              onClick={() => { setEditing(null); setForm({ ...EMPTY_SHOW }); setShowForm(true); }}
              className="bg-accent hover:bg-accent/90 text-accent-foreground rounded-xl h-12 px-6 text-base font-semibold"
            >
              <Plus className="w-5 h-5 mr-1.5" /> New Show
            </Button>
          )}
        </div>

        {/* The actual show switcher. Deliberately not on the Home screen: it
            is a rare action (once a day at most) that would otherwise sit
            as an always visible control there, and a wrong tap silently
            misfiles every contact captured after it. */}
        {/* "Currently Selected Show:" label, task: 2026-09-25. Distinct from
            Home's own "Current Show Viewing:" label (HomeHeader.jsx), a
            different page and a different piece of text on purpose: this
            one names what the dropdown right below it is set to, not what a
            rep is looking at on Home. */}
        <p className="text-base font-bold text-foreground text-center mb-1.5">Currently Selected Show:</p>
        <div className="mb-5">
          <ShowSelector allowAll />
        </div>

        <AnimatePresence>
          {showForm && (
            <motion.div
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -10 }}
              className="bg-card rounded-2xl p-5 shadow-sm mb-5 border border-border"
            >
              <h3 className="font-semibold text-foreground mb-4">{editing ? 'Edit Show' : 'New Trade Show'}</h3>
              {editingIsGeneral && (
                <div className="flex items-start gap-2 text-[11px] text-muted-foreground bg-secondary/40 rounded-lg p-2.5 mb-3">
                  <Lock className="w-3.5 h-3.5 mt-0.5 shrink-0" />
                  <span>
                    This is a personal general bucket, the permanent catch-all for contacts met outside a
                    trade show. Its name, dates and active status are fixed and cannot be changed here, but
                    its wording and industry can be.
                  </span>
                </div>
              )}
              <form onSubmit={handleSubmit} className="space-y-3">
                <div>
                  <Label className="text-xs uppercase tracking-wider text-muted-foreground">
                    Show Name <span className="text-destructive">*</span>
                  </Label>
                  <Input
                    value={form.name}
                    onChange={e => setForm(p => ({ ...p, name: e.target.value }))}
                    placeholder="e.g. HITEC 2026"
                    disabled={editingIsGeneral}
                    className="mt-1 bg-secondary/50 border-0 disabled:opacity-60"
                  />
                  <p className="text-[10px] text-destructive mt-1">Required</p>
                </div>
                <div>
                  <Label className="text-xs uppercase tracking-wider text-muted-foreground">
                    Location {!editingIsGeneral && <span className="text-destructive">*</span>}
                  </Label>
                  <Input value={form.location} onChange={e => setForm(p => ({ ...p, location: e.target.value }))} placeholder="e.g. San Antonio, TX" className="mt-1 bg-secondary/50 border-0" />
                  {!editingIsGeneral && <p className="text-[10px] text-destructive mt-1">Required. Shown in the app header.</p>}
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label className="text-xs uppercase tracking-wider text-muted-foreground">
                      Start Date {!editingIsGeneral && <span className="text-destructive">*</span>}
                    </Label>
                    <Input
                      type="date"
                      value={form.start_date}
                      onChange={e => setForm(p => ({ ...p, start_date: e.target.value }))}
                      disabled={editingIsGeneral}
                      className="mt-1 bg-secondary/50 border-0 disabled:opacity-60"
                    />
                  </div>
                  <div>
                    <Label className="text-xs uppercase tracking-wider text-muted-foreground">
                      End Date {!editingIsGeneral && <span className="text-destructive">*</span>}
                    </Label>
                    <Input
                      type="date"
                      value={form.end_date}
                      onChange={e => setForm(p => ({ ...p, end_date: e.target.value }))}
                      disabled={editingIsGeneral}
                      className="mt-1 bg-secondary/50 border-0 disabled:opacity-60"
                    />
                  </div>
                </div>
                <div>
                  <Label className="text-xs uppercase tracking-wider text-muted-foreground">Industry</Label>
                  <Select
                    value={form.industry || '__none__'}
                    onValueChange={v => setForm(p => ({
                      ...p,
                      industry: v === '__none__' ? '' : v,
                      // Switching away from Other clears the free text name,
                      // matching what the API does on save, so the form never
                      // shows stale text the save would silently drop.
                      industry_other: v === 'other' ? p.industry_other : '',
                    }))}
                  >
                    <SelectTrigger className="mt-1 bg-secondary/50 border-0 h-10">
                      <SelectValue placeholder="No industry selected" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="__none__">No industry selected</SelectItem>
                      {INDUSTRY_OPTIONS.map(opt => (
                        <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <p className="text-[10px] text-muted-foreground mt-1">
                    Metadata only for now, not used to pick message wording yet.
                  </p>
                  {form.industry === 'other' && (
                    <div className="mt-2">
                      <Label className="text-xs uppercase tracking-wider text-muted-foreground">
                        Name this industry <span className="text-destructive">*</span>
                      </Label>
                      <Input
                        value={form.industry_other}
                        onChange={e => setForm(p => ({ ...p, industry_other: e.target.value }))}
                        placeholder="e.g. Marinas"
                        className="mt-1 bg-secondary/50 border-0"
                      />
                      <p className="text-[10px] text-destructive mt-1">Required</p>
                    </div>
                  )}
                </div>
                <div>
                  <Label className="text-xs uppercase tracking-wider text-muted-foreground">Show document URL</Label>
                  <Input
                    value={form.doc_url}
                    onChange={e => setForm(p => ({ ...p, doc_url: e.target.value }))}
                    placeholder="https://"
                    className="mt-1 bg-secondary/50 border-0"
                  />
                  <p className="text-[10px] text-muted-foreground mt-1">
                    Any URL. The schedule and travel doc for this show, so reps can open it from their phone.
                  </p>
                </div>
                <div>
                  <Label className="text-xs uppercase tracking-wider text-muted-foreground">Show document name</Label>
                  <Input
                    value={form.doc_name}
                    onChange={e => setForm(p => ({ ...p, doc_name: e.target.value }))}
                    placeholder="e.g. HITEC 2026 Schedule"
                    className="mt-1 bg-secondary/50 border-0"
                  />
                  <p className="text-[10px] text-muted-foreground mt-1">
                    What the document button is labeled. Falls back to "Show document" when blank.
                  </p>
                </div>
                <div>
                  <Label className="text-xs uppercase tracking-wider text-muted-foreground">Notes</Label>
                  <Input value={form.notes} onChange={e => setForm(p => ({ ...p, notes: e.target.value }))} placeholder="Optional notes" className="mt-1 bg-secondary/50 border-0" />
                </div>
                <div>
                  <Label className="text-xs uppercase tracking-wider text-muted-foreground">Meeting topics hint</Label>
                  <Textarea
                    value={form.meeting_topics_placeholder}
                    onChange={e => setForm(p => ({ ...p, meeting_topics_placeholder: e.target.value }))}
                    placeholder="e.g. Discuss the guest feedback platform"
                    rows={1}
                    className="mt-1 bg-secondary/50 border-0 resize-none overflow-hidden min-h-0"
                  />
                  <p className="text-[10px] text-muted-foreground mt-1">
                    Shown as a grey hint in the Meeting Topics box at capture, for this show only. Never
                    saved on its own, a rep still has to type the real topic. Leave blank for no hint.
                  </p>
                </div>
                {/* Which reps the shared device's picker offers while
                    capturing under this show (task: per-show rep assignment,
                    2026-09-15). Does not touch which shows any rep can see
                    or pick from their own login, every rep still sees every
                    show either way, this only narrows the device picker. A
                    personal general bucket has exactly one owner already,
                    so this makes no sense there and is hidden. */}
                {!editingIsGeneral && isAdmin && (
                  <div className="pt-1 border-t border-border/50">
                    <Label className="text-xs uppercase tracking-wider text-muted-foreground flex items-center gap-1.5">
                      <Users className="w-3.5 h-3.5" />
                      Reps on the shared device for this show
                    </Label>
                    {editing ? (
                      <>
                        <div className="mt-2 space-y-1.5">
                          {allReps.map((rep) => (
                            <label key={rep.id} className="flex items-center gap-2 cursor-pointer">
                              <input
                                type="checkbox"
                                checked={memberIds.includes(rep.id)}
                                onChange={(e) =>
                                  setMemberIds((prev) =>
                                    e.target.checked ? [...prev, rep.id] : prev.filter((id) => id !== rep.id),
                                  )
                                }
                                className="w-4 h-4 accent-amber-500"
                              />
                              <span className="text-sm text-foreground">{rep.full_name}</span>
                            </label>
                          ))}
                          {allReps.length === 0 && (
                            <p className="text-xs text-muted-foreground">No reps to assign yet.</p>
                          )}
                        </div>
                        <p className="text-[10px] text-muted-foreground mt-2">
                          {memberIds.length === 0
                            ? 'None checked: the shared device offers every rep for this show, same as today.'
                            : `Only these ${memberIds.length} rep${memberIds.length === 1 ? '' : 's'} will be offered on the shared device for this show. Every rep still sees this show from their own login either way.`}
                        </p>
                      </>
                    ) : (
                      <p className="flex items-center gap-1.5 text-xs font-medium text-muted-foreground py-2">
                        <Users className="w-3.5 h-3.5" />
                        Create this show first to assign reps
                      </p>
                    )}
                  </div>
                )}

                {/* is_active is archived or not, despite what this used to be
                    called. Several shows are open at once and personal buckets
                    are rows in the same table, so there is no single "active
                    show" to set. Which show a rep captures into is the picker
                    on the home screen, stored per device. */}
                <div className="flex items-center gap-3 pt-1">
                  <label className={`flex items-start gap-2 ${editingIsGeneral ? 'cursor-not-allowed opacity-60' : 'cursor-pointer'}`}>
                    <input
                      type="checkbox"
                      checked={form.is_active}
                      disabled={editingIsGeneral}
                      onChange={e => setForm(p => ({ ...p, is_active: e.target.checked }))}
                      className="w-4 h-4 mt-0.5 accent-amber-500"
                    />
                    <span>
                      <span className="text-sm font-medium text-foreground">Show this event in the app</span>
                      <span className="block text-[11px] text-muted-foreground">
                        {editingIsGeneral
                          ? 'A personal general bucket can never be archived, so a rep always has somewhere to put an offline contact.'
                          : 'Unticked, it is archived: hidden from the show picker and the lists, with its contacts kept.'}
                      </span>
                    </span>
                  </label>
                </div>
                <div className="flex gap-2 pt-1">
                  <Button type="button" variant="outline" onClick={() => { setShowForm(false); setEditing(null); }} className="flex-1 min-h-10">
                    <X className="w-4 h-4 mr-1" /> Cancel
                  </Button>
                  <Button type="submit" disabled={saveMutation.isPending || setMembersMutation.isPending} className="flex-1 min-h-10 bg-accent hover:bg-accent/90 text-accent-foreground font-semibold">
                    <Check className="w-4 h-4 mr-1" /> {editing ? 'Save' : 'Create'}
                  </Button>
                </div>
              </form>

              {/* Moved to the very bottom of the Edit Show card, below Save/
                  Cancel too, and turned into a full-width button (task:
                  prominent "Edit message wording" button, 2026-09-24). Was a
                  small inline text link in the middle of the form; sized to
                  match Save's own visual weight instead of reading as a
                  subtle secondary action. Same destination and back
                  navigation as before: /templates?show=<id>, round-tripped
                  back to this exact show's own open Edit Show form by the
                  ?show= handling above this component. */}
              {editing ? (
                <Link to={`/templates?show=${editing}`} className="block mt-3">
                  <Button type="button" className="w-full min-h-10 bg-accent hover:bg-accent/90 text-black font-semibold">
                    <FileText className="w-4 h-4 mr-2" />
                    Edit message wording for this show
                  </Button>
                </Link>
              ) : (
                <p className="flex items-center gap-1.5 text-xs font-medium text-muted-foreground py-2 mt-3">
                  <FileText className="w-3.5 h-3.5" />
                  Create this show first to word its messages
                </p>
              )}
              <p className="text-[10px] text-muted-foreground mt-1.5">
                The text message and all four emails (Initial, First and Second Follow Up, Last Email), with a
                live preview against fake data. Blank there falls through to the rep's own templates, then the
                global default, then the built in wording.
              </p>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Named by label, not by field key, because the field key means
            nothing to whoever is looking at this. Item 2: the previous
            failure mode let a required field block Save with zero
            indication of which one, or that it existed at all. */}
        <Dialog open={missingFields.length > 0} onOpenChange={(open) => { if (!open) setMissingFields([]); }}>
          <DialogContent className="max-w-sm">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <AlertTriangle className="w-5 h-5 text-destructive" />
                Missing required fields
              </DialogTitle>
              <DialogDescription>
                Fill these in before saving:
              </DialogDescription>
            </DialogHeader>
            <ul className="space-y-1.5">
              {missingFields.map((field) => (
                <li key={field.key} className="flex items-center gap-2 text-sm text-foreground">
                  <span className="w-1.5 h-1.5 rounded-full bg-destructive shrink-0" />
                  {field.label}
                </li>
              ))}
            </ul>
            <DialogFooter>
              <Button onClick={() => setMissingFields([])} className="w-full">Got it</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        <SheetPanel isAdmin={isAdmin} />

        <div className="space-y-3">
          {isLoading ? (
            Array(2).fill(0).map((_, i) => <div key={i} className="bg-card rounded-xl p-4 animate-pulse h-20" />)
          ) : shows.length === 0 ? (
            <div className="text-center py-16">
              <Calendar className="w-12 h-12 text-muted-foreground/30 mx-auto mb-3" />
              <p className="text-muted-foreground font-medium">No shows yet</p>
              <p className="text-sm text-muted-foreground/60 mt-1">Create your first trade show above</p>
            </div>
          ) : (
            shows.map((show, i) => (
              <motion.div key={show.id} initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.04 }}>
                <Card className="p-4 bg-card">
                  <div className="flex items-start justify-between gap-3">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1">
                        <h3 className="font-semibold text-foreground">{show.name}</h3>
                        {show.is_active && <Badge className="bg-accent/20 text-accent border-accent/30 text-[10px] px-1.5">Active</Badge>}
                        {show.is_general && (
                          <Badge variant="outline" className="text-[10px] px-1.5 gap-1">
                            <Lock className="w-2.5 h-2.5" />Personal
                          </Badge>
                        )}
                      </div>
                      {show.location && (
                        <div className="flex items-center gap-1.5 text-xs text-muted-foreground mb-0.5">
                          <MapPin className="w-3 h-3" />{show.location}
                        </div>
                      )}
                      {(show.start_date || show.end_date) && (
                        <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
                          <Calendar className="w-3 h-3" />
                          {formatShowDateRange(show)}
                        </div>
                      )}
                    </div>
                    {/* gap-4, not gap-1.5: the toggle is a 64px-tall tap
                        target sitting right next to the much smaller 32px
                        pencil button, close enough with the old spacing
                        that a rep could miss-tap Archive while reaching for
                        Edit. Wider gap keeps both buttons easy to tell
                        apart and to land a tap on. */}
                    <div className="flex items-center gap-4 shrink-0">
                      {show.is_general ? (
                        // Not a toggle: is_active is always true for a personal
                        // bucket and can never be changed (the edit form disables
                        // this same checkbox for the identical reason), so a
                        // toggle-shaped affordance here can only ever mislead,
                        // whichever way it were drawn. A plain Lock icon, sized
                        // like the edit button beside it rather than the 64px
                        // toggle it replaces, so this row is never forced taller
                        // than its own content just to make room for a control
                        // that was never real.
                        <TooltipProvider>
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <span
                                role="img"
                                aria-label="Permanent, cannot be archived"
                                className="h-8 w-8 flex items-center justify-center text-muted-foreground/40"
                              >
                                <Lock className="w-3.5 h-3.5" />
                              </span>
                            </TooltipTrigger>
                            <TooltipContent side="top" className="max-w-[220px] text-center">
                              This is the permanent catch-all for contacts met outside a trade show. It can't be archived.
                            </TooltipContent>
                          </Tooltip>
                        </TooltipProvider>
                      ) : (
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => toggleActive.mutate({ id: show.id, is_active: !show.is_active })}
                          className={`h-16 w-16 px-0 ${show.is_active ? 'text-accent' : 'text-muted-foreground'}`}
                          title={show.is_active ? 'Archive this event' : 'Show this event in the app'}
                        >
                          {show.is_active ? <ToggleRight style={{ width: 48, height: 48 }} /> : <ToggleLeft style={{ width: 48, height: 48 }} />}
                        </Button>
                      )}
                      <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => openEdit(show)}>
                        <Pencil className="w-3.5 h-3.5" />
                      </Button>
                    </div>
                  </div>

                  {show.doc_url && (
                    <a
                      href={show.doc_url}
                      target="_blank"
                      rel="noreferrer"
                      className="mt-3 pt-3 border-t border-border flex items-center gap-1.5 text-xs text-accent hover:underline"
                    >
                      <FileText className="w-3.5 h-3.5 shrink-0" />
                      {show.doc_name || 'Show document'}
                      <ExternalLink className="w-3 h-3" />
                    </a>
                  )}

                  {/* Stats + Export */}
                  {(() => {
                    const stats = getShowStats(show.id);
                    return (
                      <div className="mt-3 pt-3 border-t border-border">
                        <div className="flex items-center gap-3 mb-2.5">
                          <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
                            <span className="font-semibold text-foreground text-sm">{stats.total}</span> contacts
                          </div>
                          <div className="flex items-center gap-1 text-xs text-amber-600">
                            <Clock className="w-3 h-3" />{stats.pending} pending
                          </div>
                          <div className="flex items-center gap-1 text-xs text-emerald-600">
                            <CheckCircle2 className="w-3 h-3" />{stats.accepted} accepted
                          </div>
                          <div className="flex items-center gap-1 text-xs text-red-400">
                            <XCircle className="w-3 h-3" />{stats.declined} declined
                          </div>
                        </div>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => exportShowCSV(show)}
                          disabled={stats.total === 0}
                          className="h-8 text-xs w-full"
                        >
                          <Download className="w-3.5 h-3.5 mr-1.5" />Export CSV ({stats.total} contacts)
                        </Button>
                      </div>
                    );
                  })()}

                </Card>
              </motion.div>
            ))
          )}
        </div>
        <div className="mt-6 pt-4 border-t border-border space-y-3">
          <Link to="/deleted-contacts">
            <Button variant="outline" className="w-full min-h-10 text-sm text-muted-foreground">
              <Trash2 className="w-4 h-4 mr-2" />Deleted Contacts
            </Button>
          </Link>

        </div>
        <div className="h-8" />
      </PageContainer>
    </div>
  );
}